import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import checkedBox from '@/images/Checkbox.svg';
import theme from '@/theme';
import styled from '@emotion/styled';
import { ExpandMore } from '@mui/icons-material';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent, SelectProps } from '@mui/material/Select';

const StyledSelect = styled(Select)<{ value: string[] }>`
    padding: 0 12px !important;
    border-radius: 50px !important;
    color: ${theme.colors.secondary[700]};
    border: ${({ value }) => (value.length ? `1px solid ${theme.colors.secondary[900]}` : `1px solid ${theme.colors.black[300]}`)};

    :hover {
        border: 1px solid ${theme.colors.secondary[900]};
    }

    fieldset {
        border: none;
    }

    .MuiSelect-select {
        padding: 0 !important;
    }

    .MuiSvgIcon-root {
        color: ${theme.colors.secondary[900]};
    }
` as unknown as typeof Select;

const StyledChip = styled(Chip)`
    background: transparent;
    font-size: 14px;
    position: relative;

    .MuiChip-label {
        padding-left: 22px;
    }

    svg {
        color: ${theme.colors.secondary[900]};
    }
`;

const StyledStartAdornment = styled(MaskedIcon)`
    position: absolute;
    left: 0;
    top: 8px;
`;

const StyledMenuItem = styled(MenuItem)`
    margin: 6px 8px;
    padding: 0 12px;
    border-radius: 8px;
    gap: 8px;
    align-items: center;

    &.Mui-selected {
        background-color: transparent;

        :hover {
            background-color: rgba(0, 0, 0, 0.04);
        }
    }
`;

interface FilterChipProps {
    option: FilterOption;
}

const FilterChip = ({ option }: FilterChipProps) => {
    return (
        <StyledChip
            key={option.value}
            label={
                <>
                    {!!option.icon && <StyledStartAdornment src={option.icon} size="16px" />}
                    {option.label}
                </>
            }
        />
    );
};

export interface FilterOption {
    value: string;
    label: string;
    icon?: string;
}

type Props = SelectProps<string> & {
    values: FilterOption[];
    options: FilterOption[];
    id?: string;
    onChange: (e: SelectChangeEvent<string>) => void;
    valueToRender: {
        label: string;
        icon?: string;
    };
};

const EmptyBox = styled.span`
    margin-left: auto;
    height: 20px;
    width: 20px;
    border-radius: 4px;
    border: 1px solid var(--secondary-shades-500, #82807e);
`;

const CheckedBox = styled(MaskedIcon)`
    margin-left: auto;
    color: ${theme.colors.secondary.main};
`;

function CheckBox({ checked }: { checked: boolean }) {
    return checked ? <CheckedBox src={checkedBox} size="20px" /> : <EmptyBox />;
}

export const FilterChipMultiSelect = ({ id, options, values, onChange, valueToRender, ...props }: Props) => {
    return (
        <>
            <StyledSelect
                multiple
                displayEmpty
                value={values.map(option => option.value) as any}
                onChange={onChange}
                renderValue={() => <FilterChip option={{ ...valueToRender, value: '' }} />}
                IconComponent={ExpandMore}
                id={id}
                MenuProps={{ PaperProps: { sx: { borderRadius: '12px !important' } } }}
                {...props}>
                {options.map(option => {
                    const isSelected = values.some(value => value.value === option.value);

                    return (
                        <StyledMenuItem key={option.value} value={option.value}>
                            {!!option.icon && <MaskedIcon src={option.icon} size="16px" />}
                            {option.label}
                            <CheckBox checked={isSelected} />
                        </StyledMenuItem>
                    );
                })}
            </StyledSelect>
        </>
    );
};
